/**
 * Erklärkästen je Seite (Tutorial-Modus) — aufgebaut wie im Event Manager.
 *
 * Aufbau je Eintrag:
 *   zweck      — ein Satz: Wofür ist die Seite da? Steht in der Kopfzeile.
 *   abschnitte — drei bis vier Blöcke à zwei bis vier Punkte. Bewährt:
 *                „Was Du hier siehst" · „Was Du hier tust" · „Worauf Du achten
 *                musst". Der letzte Block ist der wichtigste: Eine Sperre, die
 *                man nicht kennt, kostet einen halben Nachmittag.
 *   tour       — Schlüssel der Klick-Tour, die der Kasten anbietet.
 *
 * Klartext, keine Feldnamen aus dem Code. Wer den Text liest, sitzt vor der
 * Seite und will wissen, was er als Nächstes tut. Die Schlüssel sind
 * Adressen ohne führenden Schrägstrich, Kennungen als „:id".
 */
import type { MailUser } from '../types';
import { istTeam } from './rollen';

export type HilfeAbschnitt = { titel: string; punkte: string[] };
export type HilfeText = { zweck: string; abschnitte: HilfeAbschnitt[]; tour?: string };

export const SEITEN_HILFE: Record<string, HilfeText> = {
  start: {
    zweck: 'Dein Einstieg: eine Mail vorbereiten und sehen, wo Deine Mails gerade stehen.',
    tour: 'start',
    abschnitte: [
      {
        titel: 'Was Du hier siehst',
        punkte: [
          'Oben die Abkürzungen: eine neue Mail vorbereiten, Deine Mails, die Vorlagen und die Anleitung.',
          'Unter „Was wartet worauf" steht, was bei Dir liegt (Entwürfe und Rückfragen), was bei der Redaktion wartet und was schon geplant oder verschickt ist.',
          'Darunter Deine letzten Mails mit ihrem Stand. Ein Klick öffnet sie.',
        ],
      },
      {
        titel: 'So läuft eine Mail',
        punkte: [
          'Du wählst eine Vorlage, beantwortest ein paar Fragen vorab und füllst die Felder aus — die Gestaltung macht das Mailtool.',
          'Dann wählst Du die Empfänger, schickst Dir eine Testmail und reichst die Mail ein.',
          'Die Redaktion prüft sie, fragt bei Bedarf nach und gibt sie frei. Erst dann geht sie zu Deinem Wunschtermin raus.',
        ],
      },
      {
        titel: 'Worauf Du achten musst',
        punkte: [
          'Ohne Freigabe der Redaktion geht keine Mail raus — auch keine eilige. Ausnahme: Wer das Recht „ohne Freigabe versenden" hat, verschickt nach einem letzten Check selbst.',
          'Die Redaktion sichtet montags bis freitags und braucht vier volle Werktage: Reichst Du am Montag ein, geht die Mail frühestens am Freitag raus.',
          'Ist es knapper, markierst Du die Mail als „eilt" und begründest es in einem ganzen Satz. Was bis 10 Uhr da ist, geht noch am selben Tag raus, sonst am nächsten Werktag.',
          'Steht eine Mail auf „Rückfrage", wartet die Redaktion auf Dich. Du bekommst dazu auch eine E-Mail.',
        ],
      },
    ],
  },

  dashboard: {
    zweck: 'Überblick für das Kampagnen-Team: Was wartet auf eine Freigabe, was ist geplant, was ging zuletzt raus.',
    tour: 'dashboard',
    abschnitte: [
      {
        titel: 'Was Du hier siehst',
        punkte: [
          'Die Zahlen oben: Kampagnen, versendete Mails, Gruppen und Kontakte mit Einwilligung.',
          'Der Hinweis „Warten auf Freigabe" zeigt, wie viele Mails aus Geschäftsführung und Studioleitung auf die Redaktion warten.',
          'Darunter die letzten Kampagnen mit Stand und Versandtermin.',
        ],
      },
      {
        titel: 'Was Du hier tust',
        punkte: [
          'Mit „Neue Kampagne" baust Du eine Kampagne frei im Editor. Für kurze Mails im Haus-Layout ist „Neue Mail" (Assistent) schneller.',
          'Eingereichte Mails prüfst Du unter „Freigaben".',
        ],
      },
      {
        titel: 'Worauf Du achten musst',
        punkte: [
          'Kontakte kommen jede Nacht aus dem CRM. Wer dort heute eingetragen wurde, ist morgen früh hier.',
          'Die Gruppen aus CRM-Tags werden stündlich aktualisiert.',
        ],
      },
    ],
  },

  'neue-mail': {
    zweck: 'Der Mail-Assistent: Schritt für Schritt von der Vorlage bis zum Einreichen — ohne HTML, im Randori-Layout.',
    tour: 'assistent',
    abschnitte: [
      {
        titel: 'Die Schritte',
        punkte: [
          'Vorlage: Worum geht es? Jede Vorlage ist einer Mail nachgebaut, die schon rausging.',
          'Vorab-Fragen: klären, was vorher stehen muss — Stundenplan im CRM, Link getestet, alles abgestimmt. Dazu Sportschule und Wunschtermin.',
          'Inhalt: wenige Felder ausfüllen und den Text mit den Knöpfen über dem Feld formatieren (fett, kursiv, markiert, Farbe, Größe). Unter „Gestaltung" wählst Du Bannerbild, Symbol im Kopf und wie viele Emojis. Rechts siehst Du sofort die fertige Mail.',
          'Empfänger: Die Gruppen sind schon auf Deine Sportschule gefiltert; Suche, Schnellwahl und Altersregler helfen beim Finden.',
          'Prüfen & Einreichen mit Testmail an Dich. Wer ohne Freigabe versenden darf, plant die Mail hier nach einem letzten Check selbst ein.',
        ],
      },
      {
        titel: 'Was Du hier tust',
        punkte: [
          'Unten mit „Weiter" durch die Schritte. Der Entwurf wird dabei gespeichert — Du kannst jederzeit aufhören und unter „Meine Mails" weitermachen.',
          'Bei jeder Frage steht, warum wir fragen. Dahinter steckt immer eine Abhängigkeit: Was in der Mail steht, muss am Tresen, im CRM und am Terminal genauso stimmen.',
        ],
      },
      {
        titel: 'Worauf Du achten musst',
        punkte: [
          'Ist eine Vorab-Frage mit „noch nicht" beantwortet, lässt sich die Mail nicht einreichen. Dein Entwurf bleibt gespeichert, bis alles geklärt ist.',
          'Vorlauf: vier Werktage (Mo–Fr) für die Redaktion. „eilt" gibt es nur mit Begründung — bis 10 Uhr eingereicht, geht die Mail noch am selben Tag raus, sonst am nächsten Werktag.',
          'Einzelne Adressen gibt es hier nicht — nur Empfängergruppen. So bekommen nur Kontakte mit Einwilligung die Mail, und Abmeldungen werden sicher beachtet.',
          'Die Testmail geht nur an Randori-Adressen und trägt oben den Hinweis „noch nicht freigegeben".',
        ],
      },
    ],
  },

  'meine-mails': {
    zweck: 'Alle Mails, die Du vorbereitet hast — mit ihrem Stand und dem, was als Nächstes passiert.',
    tour: 'meine-mails',
    abschnitte: [
      {
        titel: 'Was Du hier siehst',
        punkte: [
          'Jede Mail mit Betreff, Wunschtermin und Stand: Entwurf, Eingereicht, In Prüfung, Rückfrage, Geplant, Versendet oder Abgelehnt.',
          'Ein Klick öffnet die Mail: Vorschau, Verlauf mit jedem Schritt und — falls vorhanden — die Rückfrage der Redaktion.',
        ],
      },
      {
        titel: 'Was Du hier tust',
        punkte: [
          'Entwürfe und Rückfragen öffnest Du mit „Bearbeiten" wieder im Assistenten.',
          'Eine eingereichte Mail, die noch nicht freigegeben ist, kannst Du zurückziehen, um sie zu ändern. Danach reichst Du sie neu ein.',
          'Entwürfe, die Du nicht mehr brauchst, kannst Du löschen.',
        ],
      },
      {
        titel: 'Worauf Du achten musst',
        punkte: [
          'Wer gerade dran ist, steht bei jeder Mail: bei „Entwurf" und „Rückfrage" Du, bei „Eingereicht" und „In Prüfung" die Redaktion.',
          'Freigegebene und geplante Mails lassen sich nicht mehr ändern. Muss doch noch etwas anders, melde Dich bei der Redaktion.',
        ],
      },
    ],
  },

  'geplante-mails': {
    zweck: 'Alle vorbereiteten, eingeplanten und versendeten Mails auf einen Blick — nach Wochen, im Kalender und mit den Rückmeldungen der Ansprechpersonen.',
    abschnitte: [
      {
        titel: 'Was Du hier siehst',
        punkte: [
          'Die Liste sortiert nach Versandwoche: „Termin verstrichen", diese Woche, nächste Woche … und „Ohne Versandtermin". Unter „Versendet" stehen die verschickten Mails mit Zustellungen, Öffnungen und Klicks.',
          'Mit „Nach Event" ordnest Du dieselben Wochen nach dem Event-Termin statt nach dem Versandtag — das Kalenderblatt der Karte zeigt dann den Event-Tag. Im Kalender wählst Du, ob er Versandtage, Events oder beides zeigt.',
          'Je Mail: wann sie rausgeht (und ob sie in Brevo eingeplant oder angehalten ist), wann das Event ist, für welche Sportschule sie ist und an wie viele Postfächer sie geht.',
          'Unter jedem Betreff steht der nächste Schritt — mit Etikett und Symbol: „Versandbereit“ (abgestimmt, nichts offen, Empfänger da — fehlt nur noch Brevo), „Zu tun“, „Termin“ oder „Brevo“ (Du bist dran), „Wartet“ (auf Antworten oder die Freigabe) und „Erledigt“ (versendet oder in Brevo eingeplant).',
          'Dazu der Stand der Abstimmung — In Arbeit, Wartet auf Feedback, Feedback einarbeiten, Abgestimmt — und wer schon geantwortet hat: „1 von 2 Antworten offen“ bzw. „2 von 2 haben geantwortet“. Haken = „Passt so“, Sprechblase = kommentiert, Uhr = offen; Zeigen oder Antippen nennt die Namen.',
          'Was Farben und Rahmen bedeuten, erklärt „Was bedeuten Farben und Rahmen?“ über der Liste. Kurz: Grün = fertig, Orange = Du bist dran oder etwas hängt, Blau = wir warten auf andere, gestrichelt = noch nicht fest (Versandtag nur aus dem Mailnamen oder als Wunschtermin), roter Streifen links = neue, ungelesene Rückmeldungen.',
          'Der Kalender zeigt Versandtage als Briefumschlag und Events als Balken über alle Tage, die sie dauern.',
        ],
      },
      {
        titel: 'Was Du hier tust',
        punkte: [
          'Mit „Nur offene“ und „Versandbereit“ neben dem Stand (oder der Kachel „versandbereit“ oben) siehst Du nur, wo noch etwas fehlt — oder nur die Mails, die Du in Brevo einplanen oder fortsetzen kannst.',
          'Mit „Feedback anfragen" bittest Du Ansprechpersonen um ihre Meinung und schickst ihnen ihren persönlichen Link — per WhatsApp, per Mail oder als Einladung aus dem Mailtool.',
          'Mit „Termine" trägst Du geplanten Versand, Event-Termin, Stand und eine interne Notiz ein. Testmails blendest Du dort aus.',
          'Mit „Löschen" räumst Du Mails weg, die nicht mehr gebraucht werden. Sie stehen dann unter „Gelöscht" und lassen sich von dort wiederherstellen.',
          'Unter „Rückmeldungen" stehen alle Kommentare und Vorschläge, neueste zuerst. Ein Klick öffnet die Mail mit der Kommentarspalte.',
          'Wer davon erfährt: Neue Rückmeldungen gehen immer an die ganze Redaktion (Admins und wer freigeben darf) — als Sammel-Mail, egal ob ein Studio, die Geschäftsführung oder jemand aus dem Team geschrieben hat. Wer den Eintrag selbst geschrieben hat, bekommt ihn nicht. In WorkCompanion sieht die Redaktion offene Rückmeldungen und Mails, die auf die Freigabe warten, außerdem als Karte und per Push.',
          'Unter „Ansprechpersonen" legst Du Personen an, legst fest, was sie sehen, und erneuerst oder sperrst ihren Link. Die Studioaccounts der Sportschulen stehen dort automatisch („Studioaccount · automatisch") und sehen alle Mails ihrer Sportschule.',
          'Wo die Links stehen: Jede Person bekommt beim Anlegen unter „Ansprechpersonen" („Person anlegen") ihren persönlichen Link zur Übersicht — dort kopierst oder teilst Du ihn jederzeit. Den Link zu einer einzelnen Mail findest Du in der Mail selbst im Kasten „Ansprechpersonen": oben die Angefragten, darunter unter „Sieht die Mail auch" alle, die sie über ihre Sportschule oder mit „alle Mails" sehen.',
          'Als Admin öffnest Du jeden Link selbst (Knopf neben „Link kopieren") und siehst die Mails genau so wie die Person — nur zum Ansehen. Kommentieren geht dort nur für die Person selbst; Du kommentierst in der Werkstatt („In der Werkstatt öffnen"). Dein Besuch zählt nicht als „gesehen".',
        ],
      },
      {
        titel: 'Worauf Du achten musst',
        punkte: [
          'Jeder Link ist persönlich und öffnet die Mails nur, wenn die Person mit dem Google-Konto ihrer hinterlegten Adresse angemeldet ist — auch zum Ansehen. Wer den Link ohne diese Anmeldung öffnet, sieht nur die Anmeldeseite. Gerät ein Link trotzdem in falsche Hände, erstell einen neuen — der alte hört sofort auf zu funktionieren.',
          'Ansprechpersonen gibt es nur mit einer Adresse von randori-pro.de. Trag bei jeder Person die Adresse ein, mit der sie sich bei Google anmeldet.',
          'Bei Studioaccounts kommen Name, Adresse und Sportschule aus dem CRM und lassen sich hier nicht ändern. Abschalten und die Rolle beschriften geht.',
          'Empfänger stehen nur als Gruppenname und Zahl da, nie als Adressen. Stehen mehrere Gruppen an einer Mail, ist die Zahl eine Obergrenze — Brevo schickt jedem Postfach nur einmal.',
          '„In Brevo angehalten" heißt: Die Kampagne ist eingeplant, aber gestoppt. Sie geht erst raus, wenn sie in Brevo wieder fortgesetzt wird. Hast Du unter „Termine" einen Versandtag eingetragen, zeigt die Übersicht diesen; steht in Brevo noch ein anderer Termin, sagt der Hinweis am Termin welcher — dort anpassen und freigeben.',
          'Löschen nimmt eine Brevo-Kampagne im Entwurf oder angehalten mit — nach dem Wiederherstellen legst Du sie neu an. Ist die Mail in Brevo eingeplant, halte sie dort zuerst an. Versendete Mails lassen sich nicht löschen.',
        ],
      },
    ],
  },

  'geplante-mails/:id': {
    zweck: 'Die Mail-Werkstatt: Mail ansehen, Kommentare und Änderungsvorschläge einarbeiten, Versionen vergleichen — wie im Überarbeiten-Modus von Word.',
    abschnitte: [
      {
        titel: 'Was Du hier siehst',
        punkte: [
          'Links die Mail, rechts die Kommentarspalte: wer wann was geschrieben, markiert oder vorgeschlagen hat. Neue Rückmeldungen gelten als gelesen, sobald Du die Mail öffnest.',
          'Änderungsvorschläge erscheinen in der Mail durchgestrichen (weg) und unterstrichen (neu).',
          'Unter „Versionen" steht jede Inhaltsänderung mit Name und Uhrzeit.',
          'Im Kasten „Ansprechpersonen" stehen alle, die um Feedback gebeten sind, und darunter unter „Sieht die Mail auch", wer die Mail sonst über den eigenen Link sieht — über die Sportschule oder mit „alle Mails". Bei jeder Person kopierst Du den Link direkt zu dieser Mail; als Admin öffnest Du ihn auch selbst.',
        ],
      },
      {
        titel: 'Was Du hier tust',
        punkte: [
          'Jede vorgeschlagene Änderung einzeln annehmen oder ablehnen; Kommentare beantworten oder als erledigt abhaken.',
          'Text direkt in der Mail ändern, mit dem Zeichenstift etwas markieren und kommentieren.',
          'Zwei Versionen vergleichen und eine ältere wiederherstellen; Termine, Stand und Ansprechpersonen pflegen.',
          'Eine Mail, die nicht mehr gebraucht wird, oben mit „Löschen" wegräumen — sie lässt sich unter „Gelöscht" wiederherstellen.',
        ],
      },
      {
        titel: 'Worauf Du achten musst',
        punkte: [
          'Vorschläge ändern die Mail erst, wenn Du sie annimmst. Bis dahin bleibt der Text, wie er ist.',
          'Hat inzwischen jemand anderes die Mail geändert, lehnt das Mailtool Dein Speichern ab und lädt den neuen Stand — nichts wird überschrieben.',
          'Ist die Kampagne schon in Brevo, überträgst Du Änderungen danach nach Brevo. Bei einer angehaltenen Kampagne geht das nur, solange sie angehalten ist; versendete Mails lassen sich nicht mehr ändern.',
        ],
      },
    ],
  },

  vorlagen: {
    zweck: 'Die Vorlagen für Mails von Geschäftsführung und Studioleitung — nachgebaut aus den Mails der letzten Monate.',
    tour: 'vorlagen',
    abschnitte: [
      {
        titel: 'Was Du hier siehst',
        punkte: [
          'Jede Vorlage mit einem Beispiel, wie die fertige Mail aussieht, und wann Du sie nimmst.',
          'Dazu, welche Fragen vorab kommen und wie viel Vorlauf die Redaktion braucht.',
        ],
      },
      {
        titel: 'Was Du hier tust',
        punkte: [
          'Mit „Diese Vorlage nutzen" startest Du den Assistenten direkt mit der Vorlage.',
          'Die Vorschau lässt sich zwischen Computer und Handy umschalten — und zwischen den Emoji-Stufen: ohne, normal oder viele passend zum Thema.',
        ],
      },
      {
        titel: 'Worauf Du achten musst',
        punkte: [
          'Größere Events mit Anmeldung oder Zahlung laufen über das Event-Tool — von dort kommt der Mail-Entwurf schon fertig.',
          'Passt keine Vorlage, nimm „Allgemeine Info". Eine Botschaft pro Mail — lieber zwei kurze als eine lange.',
        ],
      },
    ],
  },

  freigaben: {
    zweck: 'Hier entscheidet die Redaktion über eingereichte Mails: prüfen, nachfragen, freigeben oder ablehnen.',
    tour: 'freigaben',
    abschnitte: [
      {
        titel: 'Was Du hier siehst',
        punkte: [
          '„Offen" ist die Arbeitsliste: alles, was eingereicht oder in Prüfung ist — mit Wunschtermin, Absender:in und „eilt".',
          '„Entschieden" zeigt, was freigegeben, zurückgefragt oder abgelehnt wurde und von wem. Das „Protokoll" zeigt jeden einzelnen Schritt.',
          'Zu jeder Mail: Vorschau, Empfänger mit Zahl, die Antworten auf die Vorab-Fragen und die Nachricht an die Redaktion.',
        ],
      },
      {
        titel: 'Was Du hier tust',
        punkte: [
          '„In Prüfung nehmen" zeigt den anderen, dass Du dran bist.',
          '„Freigeben" plant den Versand zum Wunschtermin (oder sofort bzw. zu einem anderen Termin) und benachrichtigt die Person.',
          '„Rückfrage" schickt die Mail mit Deiner Frage zurück; „Ablehnen" legt sie mit Begründung ab. Kleinigkeiten korrigierst Du selbst über „Im Assistenten korrigieren".',
        ],
      },
      {
        titel: 'Worauf Du achten musst',
        punkte: [
          'Die eigene Mail gibt immer jemand anderes frei (Vier-Augen-Prinzip).',
          'Hat inzwischen jemand anderes entschieden, lehnt das Mailtool Deinen Schritt ab und zeigt den neuen Stand — nichts wird überschrieben.',
          'Mit der Freigabe entsteht der Brevo-Entwurf mit den internen Verteilern. Ab dann läuft der Versand wie bei jeder Kampagne.',
        ],
      },
    ],
  },

  handbuch: {
    zweck: "So funktioniert's: der ganze Ablauf, wer wann dran ist und die häufigsten Fragen.",
    abschnitte: [
      {
        titel: 'Wofür die Seite da ist',
        punkte: [
          'Zum Nachlesen vor der ersten eigenen Mail — und wenn später etwas unklar ist.',
          'Die Erklärkästen auf den anderen Seiten fassen dasselbe kurz zusammen.',
        ],
      },
    ],
  },

  campaigns: {
    zweck: 'Alle Kampagnen: anlegen, suchen, Stand verfolgen und Statistik aufrufen.',
    abschnitte: [
      {
        titel: 'Was Du hier siehst',
        punkte: [
          'Jede Kampagne mit Betreff, Stand und — nach dem Versand — Öffnungen, Klicks und Abmeldungen.',
          'Mails aus dem Assistenten tragen zusätzlich ihren Freigabe-Stand und wer sie eingereicht hat.',
        ],
      },
      {
        titel: 'Was Du hier tust',
        punkte: [
          '„Neue Kampagne" öffnet den Editor mit freiem HTML oder einer HTML-Vorlage.',
          'Mit dem Auswahlfeld filterst Du nach Stand, das Suchfeld greift auf Name und Betreff.',
        ],
      },
      {
        titel: 'Worauf Du achten musst',
        punkte: [
          'Mails von Geschäftsführung und Studioleitung entscheidest Du unter „Freigaben", nicht hier — dort stehen die Vorab-Antworten und der Wunschtermin.',
          'Versendete Kampagnen lassen sich nicht mehr ändern.',
        ],
      },
    ],
  },

  'campaigns/:id': {
    zweck: 'Der Kampagnen-Editor: Basis-Infos, Empfänger, Gestaltung, Test und Versand.',
    abschnitte: [
      {
        titel: 'Was Du hier tust',
        punkte: [
          'Die fünf Schritte oben der Reihe nach — „Speichern & Weiter" sichert jeden Schritt.',
          'Vor dem Versand eine Testmail an Dich schicken: Sie zeigt genau das, was aus Brevo verschickt wird.',
        ],
      },
      {
        titel: 'Worauf Du achten musst',
        punkte: [
          'Die internen Verteiler (Studios und Backoffice, Studioleitungen, Redaktion, Geschäftsführung) sind bei jedem Versand dabei.',
          'Kampagnen aus dem Assistenten gehen erst nach der Freigabe unter „Freigaben" raus.',
        ],
      },
    ],
  },

  segments: {
    zweck: 'Empfängergruppen: Wer bekommt eine Mail? Gruppen filtern Kontakte nach Sportschule, Mitgliedschaft, Alter oder CRM-Tag.',
    abschnitte: [
      {
        titel: 'Was Du hier siehst',
        punkte: [
          'Alle Gruppen mit der Zahl der Postfächer. Gruppen mit Sportschule sieht auch die Studioleitung dort — im Assistenten.',
          '„CRM-Tag: …" sind die Tag-Gruppen: Wer im CRM den Tag trägt, ist drin. Sie werden stündlich aktualisiert.',
        ],
      },
      {
        titel: 'Was Du hier tust',
        punkte: [
          'Oben suchen und filtern: nach Name, Sportschule, Art der Gruppe (Mitglieder, Familien & Kinder, CRM-Tags, Ehemalige …) und mit dem Altersregler nach Alter.',
          '„Wen willst Du erreichen?" setzt die passenden Filter mit einem Klick.',
        ],
      },
      {
        titel: 'Worauf Du achten musst',
        punkte: [
          'Nur Kontakte mit Einwilligung bekommen eine Mail; Abmeldungen und unzustellbare Adressen fallen von selbst heraus.',
          'Gruppen mit „Nur Admins" sieht die Studioleitung nicht; abgelaufene Gruppen lassen sich nicht mehr wählen.',
        ],
      },
    ],
  },

  templates: {
    zweck: 'HTML-Vorlagen für den Kampagnen-Editor — im Randori-Design, jeweils ohne, mit normal vielen oder mit vielen Emojis.',
    tour: 'templates',
    abschnitte: [
      {
        titel: 'Was Du hier siehst',
        punkte: [
          'Links die Haus-Vorlagen (Lehrgang, Gürtelprüfung, Event, Camp, Newsletter) und eigene Vorlagen, rechts die Vorschau.',
          'Mit dem Schalter „Emojis" siehst Du dieselbe Vorlage ohne, mit normal vielen oder mit vielen Emojis passend zum Thema.',
        ],
      },
      {
        titel: 'Was Du hier tust',
        punkte: [
          'Verwendet werden die Vorlagen im Kampagnen-Editor: Neue Kampagne → Design → Vorlage. Dort wählst Du auch die Emoji-Stufe.',
          'Platzhalter stehen in [ECKIGEN KLAMMERN]. Der Versand-Check lässt die Mail erst raus, wenn alle ersetzt sind.',
        ],
      },
      {
        titel: 'Gut zu wissen',
        punkte: [
          'Geschäftsführung und Studioleitung arbeiten mit den Vorlagen des Assistenten (unter „Vorlagen").',
          'Die Haus-Vorlagen pflegt das Mailtool; Admins bringen sie mit „Haus-Vorlagen aktualisieren" auf den neuesten Stand. Eigene Vorlagen legen Admins mit „Eigene Vorlage" an.',
        ],
      },
    ],
  },

  contacts: {
    zweck: 'Die Kontakte aus dem CRM — jede Nacht abgeglichen, mit Einwilligung und Haushalt.',
    abschnitte: [
      {
        titel: 'Worauf Du achten musst',
        punkte: [
          'Geändert wird im CRM, nicht hier. Das Mailtool übernimmt den Stand in der Nacht.',
          'Ein Postfach steht für den ganzen Haushalt: Bei Kindern ist es das Postfach der Eltern.',
        ],
      },
    ],
  },

  abmeldungen: {
    zweck: 'Wer sich abgemeldet hat und welche Adressen nicht zustellbar sind.',
    abschnitte: [
      {
        titel: 'Worauf Du achten musst',
        punkte: [
          'Abgemeldete bekommen keine Kampagnen mehr — egal in welcher Gruppe sie stehen.',
          'Hart abgewiesene Adressen bitte im CRM korrigieren, dann sind sie beim nächsten Abgleich wieder dabei.',
        ],
      },
    ],
  },

  media: {
    zweck: 'Bilder für Mails hochladen und wiederverwenden.',
    abschnitte: [
      {
        titel: 'Gut zu wissen',
        punkte: [
          'Querformat für Titelbilder, am besten 1240 × 560 Pixel. Große Fotos vorher verkleinern.',
          'Den Link eines Bildes kannst Du im Assistenten als Titelbild eintragen.',
        ],
      },
    ],
  },

  'data-quality': {
    zweck: 'Datenqualität: fehlende Namen, doppelte Adressen, unzustellbare Postfächer.',
    abschnitte: [
      {
        titel: 'Gut zu wissen',
        punkte: [
          'Korrigiert wird im CRM; hier siehst Du, wo es sich lohnt.',
        ],
      },
    ],
  },

  settings: {
    zweck: 'Einstellungen: wer im Mailtool was darf, Absender und Freigabe.',
    tour: 'settings',
    abschnitte: [
      {
        titel: 'Was Du hier siehst',
        punkte: [
          '„Benutzer & Rechte" zeigt alle, die das Mailtool nutzen: Rolle, woher sie kommt (CRM oder hier eingetragen), Sportschulen, letzte Anmeldung und ihre Mails.',
          'Oben die Zahlen je Rolle und die Übersicht „Wer darf was?". Ein Klick auf eine Zahl filtert die Tabelle.',
        ],
      },
      {
        titel: 'Was Du hier tust',
        punkte: [
          'Rolle ändern, Sportschulen anpassen, das Freigaberecht vergeben oder jemandem erlauben, ohne Freigabe zu versenden — direkt in der Tabelle.',
          '„Person hinzufügen" trägt jemanden über die Personensuche ein, ohne User-ID. Geschäftsführung und Studioleitung bekommen ihre Rolle von selbst aus dem CRM.',
        ],
      },
      {
        titel: 'Worauf Du achten musst',
        punkte: [
          'Eine eingetragene Rolle geht vor der CRM-Rolle. „CRM-Rolle" setzt die Person wieder auf den Stand aus dem CRM.',
          '„Ohne Freigabe versenden" nur für Personen, deren Mails die Redaktion nicht mehr sichten muss — sie verschicken nach einem letzten Check selbst. Admins dürfen das immer.',
        ],
      },
    ],
  },
};

/** Text zur Adresse. Die Startseite erklärt dem Team etwas anderes als der Studioleitung. */
export function hilfeFuerPfad(pfad: string, u: MailUser | null): HilfeText | null {
  const roh = pfad.replace(/^\/+/, '').replace(/\/+$/, '');
  if (!roh) return istTeam(u) ? SEITEN_HILFE.dashboard : SEITEN_HILFE.start;

  const istKennung = (s: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s) || s === 'new';
  const teile = roh.split('/').map((t) => (istKennung(t) ? ':id' : t));

  for (let laenge = teile.length; laenge > 0; laenge--) {
    const treffer = SEITEN_HILFE[teile.slice(0, laenge).join('/')];
    if (treffer) return treffer;
  }
  return null;
}
